/** Normalize peak JSON blobs from GET /workout/{id}/details into sorted arrays. */

import {
  findStreamIndicesForRange,
  findStreamIndicesForUnixRange,
  type StreamPoint,
} from '../../utils/workoutStream';
import { lapIndexToSegmentRange, type LapRow } from '../../utils/comparison/lapSegments';

export interface PeakPoint {
  value: number;
  interval: number;
  label?: string;
  begin?: number;
  end?: number;
  start_time?: number;
  time_stamp?: number;
}

export interface PeakChartPoint {
  value: number;
  label: string;
}

function toFiniteNumber(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Object map from API → sorted peak rows (Ai-Tempo WorkoutPeaks parity). */
export function normalizePeakMap(raw: unknown): PeakPoint[] {
  if (!raw || typeof raw !== 'object') return [];
  const rows: PeakPoint[] = [];
  for (const item of Object.values(raw as Record<string, unknown>)) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const value = toFiniteNumber(row.value);
    let interval = toFiniteNumber(row.interval);
    const begin = toFiniteNumber(row.begin);
    const end = toFiniteNumber(row.end);
    if (value === null) continue;
    if (interval == null && begin != null && end != null) {
      interval = Math.max(1, Math.abs(end - begin));
    }
    if (interval == null) interval = 0;
    rows.push({
      value,
      interval,
      label: typeof row.label === 'string' ? row.label : undefined,
      begin: toFiniteNumber(row.begin) ?? undefined,
      end: toFiniteNumber(row.end) ?? undefined,
      start_time: toFiniteNumber(row.start_time) ?? toFiniteNumber(row.begin) ?? undefined,
      time_stamp: toFiniteNumber(row.time_stamp) ?? toFiniteNumber(row.end) ?? undefined,
    });
  }
  return rows.sort((a, b) => a.interval - b.interval);
}

/** Modal list rows — includes title + start/end for map highlight (Ai-Tempo mapPeak parity). */
export function mapPeakModalRows(raw: unknown): Array<PeakPoint & { title?: string }> {
  return normalizePeakMap(raw).map((item) => ({
    ...item,
    title: item.label,
    start_time: item.start_time ?? item.begin,
    time_stamp: item.time_stamp ?? item.end,
  }));
}

/** Lap rows normalized for the laps/peaks picker modal. */
export function normalizeLapModalRows(laps: unknown): Array<Record<string, unknown>> {
  if (!Array.isArray(laps)) return [];
  return laps.map((lap, idx) => {
    const item = (lap && typeof lap === 'object' ? lap : {}) as Record<string, unknown>;
    const start = toFiniteNumber(item.start_time ?? item.timestamp ?? item.begin);
    let end = toFiniteNumber(item.time_stamp ?? item.end ?? item.end_time);
    if (end == null && start != null) {
      const duration =
        toFiniteNumber(item.total_timer_time) ?? toFiniteNumber(item.total_elapsed_time);
      if (duration != null && duration > 0) end = start + duration;
    }
    return {
      ...item,
      lap: item.lap ?? idx,
      start_time: start ?? item.start_time,
      time_stamp: end ?? item.time_stamp,
    };
  });
}

export function peakIntervalLabel(seconds: number): string {
  if (!seconds) return '—';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
}

/** Peak row interval in seconds (interval field or begin/end span). */
export function peakIntervalSeconds(item: Record<string, unknown>): number | null {
  const interval = toFiniteNumber(item.interval);
  if (interval != null && interval > 0) return interval;
  const begin = toFiniteNumber(item.begin ?? item.start_time);
  const end = toFiniteNumber(item.end ?? item.time_stamp ?? item.end_time);
  if (begin != null && end != null && end > begin) return end - begin;
  return null;
}

type PeakTitleT = (key: string, params?: Record<string, string | number>) => string;

/** Bottom-sheet title when a peak row is selected — e.g. "پیک 1 دقیقه". */
export function formatPeakSelectionTitle(item: Record<string, unknown>, t: PeakTitleT): string {
  const sec = peakIntervalSeconds(item);
  if (sec != null) {
    let duration: string;
    if (sec < 60) duration = t('workoutDetail.map.peakDurationSec', { n: sec });
    else if (sec < 3600) duration = t('workoutDetail.map.peakDurationMin', { n: Math.round(sec / 60) });
    else duration = t('workoutDetail.map.peakDurationHour', { n: (sec / 3600).toFixed(1) });
    return t('workoutDetail.map.peakSelectionTitle', { duration });
  }
  const fallback = String(item.label ?? item.title ?? '').trim();
  if (fallback) return t('workoutDetail.map.peakSelectionTitle', { duration: fallback });
  return t('workoutDetail.map.selectedRange');
}

export function toPeakChartData(peaks: PeakPoint[]): PeakChartPoint[] {
  return peaks.map((item) => ({
    value: Math.round(item.value * 100) / 100,
    label: peakIntervalLabel(item.interval),
  }));
}

/** Unix start/end for a lap or peak row (Ai-Tempo WorkoutMap handleRest parity). */
export function unixRangeFromModalItem(
  item: Record<string, unknown>,
): { startUnix: number; endUnix: number } | null {
  const startRaw = item.start_time ?? item.begin ?? item.timestamp;
  const endRaw = item.time_stamp ?? item.end ?? item.end_time;
  let start = toFiniteNumber(startRaw);
  let end = toFiniteNumber(endRaw);

  if (start == null && end != null) start = end;
  if (start == null) return null;

  if (end == null) {
    const lapDuration =
      toFiniteNumber(item.total_timer_time) ?? toFiniteNumber(item.total_elapsed_time);
    if (lapDuration != null && lapDuration > 0) {
      end = start + lapDuration;
    } else {
      const interval = toFiniteNumber(item.interval);
      end = interval != null && interval > 0 ? start + interval - 1 : start;
    }
  }

  return {
    startUnix: Math.min(start, end),
    endUnix: Math.max(start, end),
  };
}

/** Resolve stream indices for a lap/peak row — unix, elapsed, or relative timestamps. */
export function findStreamIndicesForModalItem(
  stream: StreamPoint[],
  item: Record<string, unknown>,
): { startIdx: number; endIdx: number } | null {
  if (!stream.length) return null;

  const range = unixRangeFromModalItem(item);
  if (!range) return null;

  let { startUnix, endUnix } = range;
  if (endUnix <= startUnix) {
    const interval = toFiniteNumber(item.interval);
    if (interval != null && interval > 0) endUnix = startUnix + interval;
  }

  const looksUnix = startUnix > 1_000_000_000 || endUnix > 1_000_000_000;

  if (looksUnix) {
    const hit = findStreamIndicesForUnixRange(stream, startUnix, endUnix);
    if (hit && hit.endIdx >= hit.startIdx) {
      return { startIdx: hit.startIdx, endIdx: hit.endIdx };
    }
    return null;
  }

  {
    const { startIdx, endIdx } = findStreamIndicesForRange(stream, startUnix, endUnix);
    if (endIdx >= startIdx) return { startIdx, endIdx };
  }

  const firstTs = stream.find((p) => p.timestamp != null)?.timestamp;
  if (firstTs != null && firstTs > 1_000_000_000 && startUnix < 1_000_000_000) {
    const hit = findStreamIndicesForUnixRange(stream, firstTs + startUnix, firstTs + endUnix);
    if (hit && hit.endIdx >= hit.startIdx) {
      return { startIdx: hit.startIdx, endIdx: hit.endIdx };
    }
  }

  if (startUnix >= 0 && endUnix >= startUnix && endUnix < stream.length) {
    return { startIdx: Math.floor(startUnix), endIdx: Math.min(Math.floor(endUnix), stream.length - 1) };
  }

  return null;
}

/** Lap row → stream indices (cumulative lap durations first, then FIT timestamps). */
export function findStreamIndicesForLap(
  laps: LapRow[],
  lapIndex: number,
  stream: StreamPoint[],
  workoutDurationSec: number,
): { startIdx: number; endIdx: number } | null {
  if (!stream.length || lapIndex < 0 || lapIndex >= laps.length) return null;

  const seg = lapIndexToSegmentRange(laps, lapIndex, workoutDurationSec);
  if (seg) {
    const hit = findStreamIndicesForRange(stream, seg.startSec, seg.endSec);
    if (hit.endIdx >= hit.startIdx) return hit;
  }

  const normalized = normalizeLapModalRows(laps)[lapIndex];
  if (normalized) {
    return findStreamIndicesForModalItem(stream, normalized);
  }

  return null;
}

/** Whether a modal row can be highlighted on the map/chart. */
export function canHighlightModalItem(stream: StreamPoint[], item: Record<string, unknown>): boolean {
  return findStreamIndicesForModalItem(stream, item) != null;
}
